// services/storage.ts
/**
 * StorageService
 * Simulates a secure database using localStorage.
 * Handles encryption (Base64 for demo) and data separation.
 */

const DB_KEY_USERS = 'phanh_app_users_db_v1';
const DB_KEY_SESSION = 'phanh_app_session_v1';

// Simple encryption/decryption (Base64) to meet the "encryption" requirement
// In a real app, use crypto-js or similar libraries
const encrypt = (data: any): string => {
    try {
        const jsonString = JSON.stringify(data);
        return btoa(unescape(encodeURIComponent(jsonString)));
    } catch (e) {
        console.error('Encryption error', e);
        return '';
    }
};

const decrypt = (cipherText: string): any => {
    try {
        const jsonString = decodeURIComponent(escape(atob(cipherText)));
        return JSON.parse(jsonString);
    } catch (e) {
        console.error('Decryption error', e);
        return null;
    }
};

export const StorageService = {
    // --- User Management ---

    getUsers: (): any[] => {
        const data = localStorage.getItem(DB_KEY_USERS);
        if (!data) return [];
        const decrypted = decrypt(data);
        return Array.isArray(decrypted) ? decrypted : [];
    },

    saveUser: (user: any): boolean => {
        try {
            const users = StorageService.getUsers();
            // Check if user exists (by email or username)
            const exists = users.find(u => u.email === user.email || u.username === user.username);
            if (exists) {
                throw new Error('User already exists');
            }

            // Add timestamps
            const newUser = {
                ...user,
                id: Date.now().toString(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            users.push(newUser);

            // Save encrypted
            localStorage.setItem(DB_KEY_USERS, encrypt(users));
            return true;
        } catch (error) {
            console.error('Save user error:', error);
            throw error;
        }
    },

    updateUser: (updatedUser: any): boolean => {
        try {
            const users = StorageService.getUsers();
            const index = users.findIndex(u => u.id === updatedUser.id);

            if (index === -1) return false;

            users[index] = {
                ...users[index],
                ...updatedUser,
                updatedAt: new Date().toISOString()
            };

            localStorage.setItem(DB_KEY_USERS, encrypt(users));

            // Also update session if it's the current user
            const currentUser = StorageService.getSessionUser();
            if (currentUser && currentUser.id === updatedUser.id) {
                StorageService.saveSession(users[index]);
            }

            return true;
        } catch (error) {
            console.error('Update user error:', error);
            return false;
        }
    },

    findUser: (username: string): any | null => {
        const users = StorageService.getUsers();
        // Allow login by username or email
        return users.find(u => u.username === username || u.email === username) || null;
    },

    // --- Session Management ---

    saveSession: (user: any) => {
        // Don't store password in session
        const { password, ...userSafe } = user;
        localStorage.setItem(DB_KEY_SESSION, encrypt(userSafe));
    },

    getSessionUser: (): any | null => {
        const data = localStorage.getItem(DB_KEY_SESSION);
        return data ? decrypt(data) : null;
    },

    clearSession: () => {
        localStorage.removeItem(DB_KEY_SESSION);
    }
};
